/**
 * PublicidadView — Generación de piezas publicitarias via n8n (INS-PUBLICIDAD-001).
 * 6 tipos base; respuesta con 3 variantes creativas (v1, v2, v3).
 * Features: ampliar/descargar imagen, copiar copy redes, historial navegable,
 * detalles técnicos colapsables, actividad interactiva NSS5 durante generación.
 * @module presentation/views/PublicidadView
 */
import { PublicidadApiClient } from '../../infrastructure/api/PublicidadApiClient.js';
import { BusinessApiClient } from '../../infrastructure/api/BusinessApiClient.js';
import { buildLearningEngine } from '../shared/NxLearningEngine.js';
import { getSelectedBusiness, getSelectedBusinessName } from '../components/BusinessSelector.js';
import { registerPubB64Src, revokePubPreviewBlobs, isPubDataImageUrl, resolvePubDataUrl } from '../../infrastructure/pubImagePreview.js';
import { AppConfig } from '../../config.js';
import { useFeatureGate } from '../hooks/useFeatureGate.js';
import { renderFeatureGate } from '../components/FeatureGate.js';
import { avisar } from '../shared/nxDialogo.js';
import { blobDeUrl } from '../../infrastructure/descargarArchivo.js';

/** Extrae el sub (userId) del JWT sin verificación de firma. */
function jwtUserId() {
  try {
    const token = AppConfig.token;
    if (!token) return 'anon';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub || payload.uid || payload.user_id || 'anon';
  } catch { return 'anon'; }
}

const TIPOS = [
  { id: 'oferta',       label: 'Oferta / Descuento',   icon: '🏷️', desc: 'Producto con descuento, remate, martes de pollo, etc.',
    campos: [
      { key: 'producto', label: 'Producto', type: 'text', placeholder: 'Ej: Pechuga de pollo' },
      { key: 'porcentaje_descuento', label: '% Descuento', type: 'number', placeholder: '20' },
      { key: 'precio_normal', label: 'Precio normal', type: 'number', placeholder: '15000' },
      { key: 'precio_oferta', label: 'Precio oferta', type: 'number', placeholder: '12000' },
      { key: 'fecha_inicio', label: 'Desde', type: 'date' },
      { key: 'fecha_fin', label: 'Hasta', type: 'date' },
      { key: 'cta', label: 'Llamado a la acción', type: 'text', placeholder: 'Ej: ¡Corre que se acaba!' },
    ]},
  { id: 'servicio',     label: 'Servicio / Conveniencia', icon: '🚚', desc: 'Domicilios, WhatsApp, compra fácil.',
    campos: [
      { key: 'beneficio_principal', label: 'Beneficio', type: 'text', placeholder: 'Domicilios gratis' },
      { key: 'canal_pedido', label: 'Canal', type: 'text', placeholder: 'WhatsApp / Web / Teléfono' },
      { key: 'numero_whatsapp', label: 'WhatsApp', type: 'text', placeholder: '300 123 4567' },
      { key: 'cobertura', label: 'Cobertura', type: 'text', placeholder: 'Todo Ibagué' },
      { key: 'cta', label: 'CTA', type: 'text', placeholder: '¡Pide ya!' },
    ]},
  { id: 'marca',        label: 'Calidad / Marca',       icon: '⭐', desc: 'Frescura, confianza, identidad del negocio.',
    campos: [
      { key: 'atributo_marca', label: 'Atributo', type: 'text', placeholder: 'Frescura garantizada' },
      { key: 'titular', label: 'Titular', type: 'text', placeholder: 'Todo en un solo lugar' },
      { key: 'prueba_social', label: 'Prueba social', type: 'text', placeholder: '+15 años sirviéndote' },
      { key: 'cta', label: 'CTA', type: 'text', placeholder: 'Visítanos' },
    ]},
  { id: 'campana',      label: 'Campaña mayor',         icon: '🎉', desc: 'Separata, Mercamanía, sorteos, temporadas.',
    campos: [
      { key: 'nombre_campana', label: 'Nombre campaña', type: 'text', placeholder: 'Mercamanía Marzo' },
      { key: 'vigencia', label: 'Vigencia', type: 'text', placeholder: 'Del 1 al 15 de marzo' },
      { key: 'premio', label: 'Premio', type: 'text', placeholder: 'TV 55" Samsung' },
      { key: 'requisito_compra', label: 'Requisito', type: 'text', placeholder: 'Compras mayores a $50.000' },
      { key: 'mecanica', label: 'Mecánica', type: 'text', placeholder: 'Por cada $50.000 recibes un cupón' },
      { key: 'cta', label: 'CTA', type: 'text', placeholder: '¡Participa y gana!' },
    ]},
  { id: 'interaccion',  label: 'Interacción / Dinámica', icon: '💬', desc: 'Encuestas, juegos, adivina el precio.',
    campos: [
      { key: 'pregunta', label: 'Pregunta', type: 'text', placeholder: '¿Qué llevarías por $50.000?' },
      { key: 'mecanica', label: 'Mecánica', type: 'text', placeholder: 'Comenta y participa' },
      { key: 'premio', label: 'Premio', type: 'text', placeholder: 'Bono de $100.000' },
      { key: 'hashtag', label: 'Hashtag', type: 'text', placeholder: '#ElMaizalTePrecia' },
      { key: 'cta', label: 'CTA', type: 'text', placeholder: '¡Comenta ya!' },
    ]},
  { id: 'informativa',  label: 'Informativa / Educativa', icon: '📢', desc: 'Horarios, medios de pago, cómo comprar.',
    campos: [
      { key: 'tema', label: 'Tema', type: 'text', placeholder: 'Cómo comprar por WhatsApp' },
      { key: 'pasos', label: 'Pasos / Contenido', type: 'textarea', placeholder: '1. Escríbenos al 300...\n2. Envía tu lista...\n3. Recibe en casa' },
      { key: 'beneficio', label: 'Beneficio', type: 'text', placeholder: 'Sin hacer fila' },
      { key: 'cta', label: 'CTA', type: 'text', placeholder: 'Escríbenos' },
    ]},
];

// Pre-fill examples for demo (top 3 types)
const PREFILLS = {
  oferta: {
    producto: 'Pechuga de pollo fresca',
    porcentaje_descuento: '20',
    precio_normal: '15900',
    precio_oferta: '12720',
    fecha_inicio: '2026-04-14',
    fecha_fin: '2026-04-20',
    cta: 'Corre que se acaba!',
    instrucciones: 'REGLA ABSOLUTA: El producto DEBE ser exactamente PECHUGA DE POLLO, NO otro producto. El precio oferta DEBE ser $12.720 y el normal $15.900. NO sustituir, NO inventar, NO cambiar el producto ni los precios. Tono urgente pero cercano. Precios en pesos colombianos.'
  },
  marca: {
    atributo_marca: 'Frescura garantizada',
    titular: 'Lo que compras hoy, llego hoy',
    prueba_social: 'Mas de 37 anos acompanandote',
    cta: 'Visitanos!',
    instrucciones: 'IMPORTANTE: Usar el personaje/mascota del negocio de forma prominente. Tono familiar, de barrio, que transmita confianza y cercanía. El personaje debe estar saludando o interactuando con el viewer.'
  },
  campana: {
    nombre_campana: 'Mercamania El Maizal',
    vigencia: 'Del 14 al 30 de abril 2026',
    premio: 'Bono para mercar por $500.000',
    requisito_compra: 'Por cada $50.000 en compras',
    mecanica: 'Acumula un cupon por cada $50.000 y participa en el sorteo del 30 de abril',
    cta: 'Participa y gana!',
    instrucciones: 'Estilo festivo, energia de celebracion. Mostrar visualmente la canasta de premio. Confetti y estrellas pero sin saturar. Tipografia 3D bold para el nombre de la campaña.'
  }
};

// Deuda 4: este escape (no cambiaba la comilla simple) es ahora el compartido.
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

function variantImageSrc(x) {
  if (!x || typeof x !== 'object') return '';
  return x.b64 || x.image_url || x.url || x.imagen_url || x.fondo_url || '';
}

function pickBizAccount(biz) {
  const cfg = biz?.accountConfig ?? biz?.account_config ?? {};
  return {
    logoUrl: cfg.logo_url || cfg.logoUrl || '',
    colorPrimario: cfg.color_primario || cfg.colorPrimario || '',
    colorSecundario: cfg.color_secundario || cfg.colorSecundario || '',
    colorTexto: cfg.color_texto || cfg.colorTexto || '',
    personajeUrl: cfg.personaje_url || cfg.personajeUrl || '',
    personajeCategoria: cfg.personaje_categoria || cfg.personajeCategoria || 'marca',
    fontName: cfg.font_name || cfg.fontName || '',
    fontUrl: cfg.font_url || cfg.fontUrl || '',
    businessType: biz?.businessType ?? biz?.business_type ?? '',
  };
}

/* ─── NSS5: Banco de preguntas para actividad interactiva durante generación ───
 * Formato: { q, opts: [op0, op1, op2, op3], a: índice_correcto, cat }
 * La respuesta correcta está en distintas posiciones (NO siempre en 0).
 * Categorías: 'marketing' | 'productividad'
 * ─────────────────────────────────────────────────────────────────────────── */

const QUIZ_BANK = [
  /* ── MARKETING DIGITAL ── */
  { q: '¿Qué significa CTA en marketing?', cat: 'marketing',
    opts: ['Click Through Ads', 'Call To Action', 'Creative Text Art', 'Customer Target Analysis'], a: 1 },
  { q: '¿Cuál es el formato más usado para Stories e Instagram Reels?', cat: 'marketing',
    opts: ['1080x1080 px', '1920x1080 px', '1080x1920 px', '800x1200 px'], a: 2 },
  { q: '¿Qué red social tiene mayor crecimiento de usuarios en 2026?', cat: 'marketing',
    opts: ['Facebook', 'LinkedIn', 'Twitter/X', 'TikTok'], a: 3 },
  { q: '¿Qué es el "copy" en publicidad?', cat: 'marketing',
    opts: ['El logo de la marca', 'El presupuesto del ad', 'La copia de seguridad', 'El texto persuasivo de un anuncio'], a: 3 },
  { q: '¿Cuál es la regla de los 3 segundos en redes sociales?', cat: 'marketing',
    opts: ['Publicar cada 3 segundos', 'Responder en 3 seg', 'Captar atención en los primeros 3 segundos', 'Usar 3 hashtags máximo'], a: 2 },
  { q: '¿Qué significa ROI?', cat: 'marketing',
    opts: ['Rate Of Interest', 'Reach Of Impact', 'Revenue Over Income', 'Return On Investment'], a: 3 },
  { q: '¿Qué es un "lead" en marketing digital?', cat: 'marketing',
    opts: ['Un formato de imagen', 'Un contacto potencial interesado', 'Un tipo de anuncio', 'Una red social'], a: 1 },
  { q: '¿Cuál color psicológicamente transmite urgencia en ofertas?', cat: 'marketing',
    opts: ['Azul', 'Verde', 'Rojo', 'Morado'], a: 2 },
  { q: '¿Qué es A/B testing en marketing?', cat: 'marketing',
    opts: ['Una metodología de programación', 'Un framework de diseño', 'Un tipo de base de datos', 'Comparar dos versiones para ver cuál funciona mejor'], a: 3 },
  { q: '¿Cuántos caracteres permite un post en X (Twitter)?', cat: 'marketing',
    opts: ['140', '500', '280', '160'], a: 2 },
  { q: '¿Qué es el "engagement rate"?', cat: 'marketing',
    opts: ['Velocidad de carga del sitio', 'Tasa de interacción con el contenido', 'Número de seguidores nuevos', 'Cantidad de publicaciones'], a: 1 },
  { q: '¿Qué tipo de contenido genera más engagement en redes sociales?', cat: 'marketing',
    opts: ['Texto largo', 'Documentos PDF', 'Video corto', 'Enlaces externos'], a: 2 },
  { q: '¿Qué es SEO?', cat: 'marketing',
    opts: ['Servicio especial de ofertas', 'Sistema de envío online', 'Software de edición óptica', 'Optimización para motores de búsqueda'], a: 3 },
  { q: '¿Cuál es la mejor hora para publicar en redes en Colombia?', cat: 'marketing',
    opts: ['6am - 7am', '12pm - 2pm y 7pm - 9pm', '3am - 5am', '11pm - 1am'], a: 1 },
  { q: '¿Qué porcentaje de descuento capta más atención visual?', cat: 'marketing',
    opts: ['Menos del 5%', 'Más del 90%', 'Exactamente 100%', 'Del 20% al 50%'], a: 3 },
  { q: '¿Qué es el "funnel" o embudo de ventas?', cat: 'marketing',
    opts: ['Una herramienta de diseño', 'Un tipo de anuncio pagado', 'El proceso de convertir desconocidos en clientes', 'Una red social para ventas'], a: 2 },
  { q: '¿Qué significa KPI?', cat: 'marketing',
    opts: ['Key Performance Indicator', 'Knowledge Process Integration', 'Keep Posting Influence', 'Key Public Index'], a: 0 },
  { q: '¿Cuál es la frecuencia recomendada para publicar en Instagram?', cat: 'marketing',
    opts: ['1 vez al mes', 'Varias veces al día siempre', '3 a 7 veces por semana', 'Solo en festivos'], a: 2 },
  { q: '¿Qué es el "remarketing"?', cat: 'marketing',
    opts: ['Crear una nueva campaña', 'Impactar de nuevo a usuarios que ya visitaron tu sitio', 'Cambiar el nombre de marca', 'Publicar en varios idiomas'], a: 1 },
  { q: '¿Qué red es más efectiva para B2B (negocios con negocios)?', cat: 'marketing',
    opts: ['TikTok', 'Instagram', 'Snapchat', 'LinkedIn'], a: 3 },
  { q: '¿Cuántos hashtags recomienda Instagram usar por publicación?', cat: 'marketing',
    opts: ['50 o más', 'Ninguno', 'Entre 3 y 10', 'Exactamente 30'], a: 2 },
  { q: '¿Qué es el "alcance orgánico"?', cat: 'marketing',
    opts: ['Usuarios que ven tu contenido sin pagarle a la plataforma', 'Seguidores que compraron algo', 'Personas que comparten tu post', 'Vistas de publicidad pagada'], a: 0 },
  { q: '¿Qué elemento visual mejora más las conversiones en un anuncio?', cat: 'marketing',
    opts: ['Mucho texto', 'Colores muy oscuros', 'Botón de llamado a la acción (CTA) claro', 'Fuente muy pequeña'], a: 2 },

  /* ── PRODUCTIVIDAD EMPRESARIAL ── */
  { q: '¿Qué es la Regla de Pareto (80/20) en productividad?', cat: 'productividad',
    opts: ['El 80% de resultados viene del 20% de acciones', 'Trabajar 80 horas en 20 días', 'Delegar el 80% de tareas', 'Descansar el 20% del tiempo'], a: 0 },
  { q: '¿Qué significa "timeboxing"?', cat: 'productividad',
    opts: ['Guardar documentos en cajas', 'Asignar un tiempo fijo a cada tarea', 'Trabajar horas extra', 'Reunirse cada hora'], a: 1 },
  { q: '¿Cuál es el objetivo de una reunión de "stand-up"?', cat: 'productividad',
    opts: ['Hacer ejercicio en la oficina', 'Actualización corta del equipo (máx 15 min)', 'Revisar presentaciones largas', 'Capacitar al personal'], a: 1 },
  { q: '¿Qué es el método GTD (Getting Things Done)?', cat: 'productividad',
    opts: ['Un software de contabilidad', 'Un sistema para capturar y procesar tareas pendientes', 'Una red social empresarial', 'Una técnica de meditación'], a: 1 },
  { q: '¿Qué es la técnica Pomodoro?', cat: 'productividad',
    opts: ['25 min de trabajo + 5 min de descanso en ciclos', 'Trabajar sin parar 4 horas', 'Cocinar durante el trabajo', 'Revisar el correo cada 25 min'], a: 0 },
  { q: '¿Cuál es el mayor ladrón de tiempo en las empresas?', cat: 'productividad',
    opts: ['Tomar descansos', 'Reuniones innecesarias o mal organizadas', 'Usar computador', 'Escribir correos'], a: 1 },
  { q: '¿Qué es el indicador OKR?', cat: 'productividad',
    opts: ['Objectives and Key Results', 'Online Knowledge Resource', 'Operations Keep Running', 'Output Key Review'], a: 0 },
  { q: '¿Qué es la "procrastinación"?', cat: 'productividad',
    opts: ['Trabajar muy rápido', 'Delegar tareas eficientemente', 'Postergar tareas importantes por las menos urgentes', 'Llegar temprano a la oficina'], a: 2 },
  { q: '¿Cuál es la diferencia entre urgente e importante?', cat: 'productividad',
    opts: ['No hay diferencia', 'Urgente requiere atención inmediata; importante tiene alto impacto a futuro', 'Urgente es más importante siempre', 'Importante es lo que se hace primero'], a: 1 },
  { q: '¿Qué es la Matriz de Eisenhower?', cat: 'productividad',
    opts: ['Un organigrama de empresa', 'Una herramienta para priorizar tareas por urgencia e importancia', 'Un sistema de pagos', 'Un método de facturación'], a: 1 },
  { q: '¿Qué significa "delegación efectiva"?', cat: 'productividad',
    opts: ['Darle el trabajo a alguien sin explicar nada', 'Asignar tareas dando autoridad, recursos y claridad de resultado', 'No trabajar nunca', 'Supervisar cada segundo al equipo'], a: 1 },
  { q: '¿Cuántas horas productivas reales tiene un trabajador en un día de 8 horas?', cat: 'productividad',
    opts: ['8 horas exactas', 'Menos de 1 hora', 'Alrededor de 3 a 4 horas', 'Exactamente 6 horas'], a: 2 },
  { q: '¿Qué es el "deep work" (trabajo profundo)?', cat: 'productividad',
    opts: ['Trabajar desde el sótano', 'Hacer muchas cosas a la vez', 'Actividad cognitiva sin distracciones para resultados de alta calidad', 'Quedarse hasta tarde en la oficina'], a: 2 },
  { q: '¿Cuál es la ventaja de hacer listas de tareas (to-do lists)?', cat: 'productividad',
    opts: ['Permiten recordar compromisos y medir avance', 'Son inútiles en el trabajo moderno', 'Solo funcionan para tareas del hogar', 'Consumen tiempo sin beneficio'], a: 0 },
  { q: '¿Qué es el "batch processing" en productividad?', cat: 'productividad',
    opts: ['Procesar archivos en computador', 'Agrupar tareas similares para hacerlas juntas y ahorrar tiempo', 'Reunirse en grupos de trabajo', 'Repartir trabajo entre el equipo'], a: 1 },
  { q: '¿Cuál es el beneficio de establecer rutinas de trabajo?', cat: 'productividad',
    opts: ['Reducen la flexibilidad laboral', 'Eliminan la creatividad', 'Reducen la fatiga de decisión y aumentan la consistencia', 'Solo son útiles para trabajos manuales'], a: 2 },
  { q: '¿Qué es el "Single-Tasking"?', cat: 'productividad',
    opts: ['Tener un solo empleado', 'Enfocarse en una sola tarea a la vez para mayor eficiencia', 'Usar una sola aplicación de trabajo', 'Trabajar en un solo proyecto por año'], a: 1 },
  { q: '¿Cuál es el propósito de una revisión semanal de objetivos?', cat: 'productividad',
    opts: ['Criticar al equipo', 'Evaluar progreso, ajustar prioridades y planificar la semana siguiente', 'Reportar horas trabajadas', 'Revisar el correo electrónico'], a: 1 },
  { q: '¿Qué hábito aumenta la productividad en un negocio pequeño?', cat: 'productividad',
    opts: ['Atender todas las solicitudes en tiempo real', 'Responder WhatsApp cada 5 minutos', 'Revisar el correo cada hora', 'Definir y respetar bloques de tiempo para tareas clave'], a: 3 },
  { q: '¿Qué es la "automatización de procesos" en un negocio?', cat: 'productividad',
    opts: ['Reemplazar a todos los empleados', 'Usar tecnología para ejecutar tareas repetitivas sin intervención humana', 'Comprar maquinaria industrial', 'Tercerizar el servicio al cliente'], a: 1 },
];

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/* ─── NSS5 Quiz: seguimiento por usuario via localStorage ─── */

const NSS5_STORAGE_KEY = 'nss5_quiz_progress';

function nss5LoadProgress(userId) {
  try {
    const raw = localStorage.getItem(NSS5_STORAGE_KEY);
    if (!raw) return null;
    const all = JSON.parse(raw);
    return all[userId] ?? null;
  } catch { return null; }
}

function nss5SaveProgress(userId, data) {
  try {
    const raw = localStorage.getItem(NSS5_STORAGE_KEY);
    const all = raw ? JSON.parse(raw) : {};
    all[userId] = { ...data, updatedAt: Date.now() };
    localStorage.setItem(NSS5_STORAGE_KEY, JSON.stringify(all));
  } catch { /* localStorage no disponible */ }
}

/**
 * NSS5 Activity Engine: módulo intercambiable de actividad durante espera.
 * Actualmente: quiz de marketing + productividad. Diseñado para ser reemplazable
 * por puzzles, trivias temáticas, actividades según plan de estudios, etc.
 * @param {HTMLElement} containerEl
 * @param {string} [userId] - ID del usuario logueado (para persistencia)
 */
function buildNss5ActivityEngine(containerEl, userId) {
  const uid = userId || 'anon';
  const saved = nss5LoadProgress(uid);
  const questions = shuffleArray(QUIZ_BANK.filter(q => q.cat === 'marketing'));

  let idx = 0;
  let score = saved?.score ?? 0;
  let answered = saved?.answered ?? 0;
  let selectedOpt = null;

  function persist() {
    nss5SaveProgress(uid, { score, answered });
  }

  function catLabel(cat) {
    return cat === 'productividad' ? '📋 Productividad' : '📣 Marketing Digital';
  }

  function renderQuestion() {
    if (idx >= questions.length) idx = 0;
    const q = questions[idx];
    selectedOpt = null;
    containerEl.innerHTML = `
      <div class="nss5-quiz-wrap">
        <div class="nss5-quiz-header">
          <span class="nss5-quiz-badge">Mientras esperas…</span>
          <span class="nss5-quiz-score">${score}/${answered} correctas</span>
        </div>
        <div class="nss5-quiz-question">${esc(q.q)}</div>
        <div class="nss5-quiz-opts">
          ${q.opts.map((o, i) => `
            <button type="button" class="nss5-quiz-opt" data-idx="${i}">${esc(o)}</button>
          `).join('')}
        </div>
        <div class="nss5-quiz-footer">
          <span class="nss5-quiz-cat">${catLabel(q.cat)}</span>
          <span class="nss5-quiz-progress">Pregunta ${answered + 1}</span>
        </div>
      </div>`;
    containerEl.querySelectorAll('.nss5-quiz-opt').forEach(btn => {
      btn.addEventListener('click', () => handleAnswer(btn, q));
    });
  }

  function handleAnswer(btn, q) {
    if (selectedOpt !== null) return;
    const chosen = parseInt(btn.dataset.idx, 10);
    selectedOpt = chosen;
    answered++;
    const correct = chosen === q.a;
    if (correct) score++;
    persist();
    containerEl.querySelectorAll('.nss5-quiz-opt').forEach((b, i) => {
      b.classList.add('nss5-quiz-opt--locked');
      if (i === q.a) b.classList.add('nss5-quiz-opt--correct');
      if (i === chosen && !correct) b.classList.add('nss5-quiz-opt--wrong');
    });
    const scoreEl = containerEl.querySelector('.nss5-quiz-score');
    if (scoreEl) scoreEl.textContent = `${score}/${answered} correctas`;
    setTimeout(() => { idx++; renderQuestion(); }, 1400);
  }

  renderQuestion();
  return { destroy() { containerEl.innerHTML = ''; } };
}


function buildResultSection(data) {
  revokePubPreviewBlobs();
  const d = data?.data ?? data;
  if (!d) return '<p class="pub-hist-empty">Sin datos.</p>';

  const meta = [];
  if (d.instruction_id) meta.push(`<code>${esc(d.instruction_id)}</code>`);
  if (d.variant_key_used) meta.push(`variante <strong>${esc(d.variant_key_used)}</strong>`);
  if (d.tipo_publicidad) meta.push(`tipo <strong>${esc(d.tipo_publicidad)}</strong>`);
  const metaLine = meta.length
    ? `<p class="pub-result-meta">Catálogo n8n: ${meta.join(' · ')} · Editá prompts en <strong>Instrucciones</strong> (INS-PUBLICIDAD-001).</p>`
    : '';

  if (d.v1 || d.v2 || d.v3) {
    const slots = [
      { key: 'v1', def: 'Variante 1 — Enfoque A' },
      { key: 'v2', def: 'Variante 2 — Enfoque B' },
      { key: 'v3', def: 'Variante 3 — Enfoque C' },
    ];
    const cards = slots.map(({ key, def }) => {
      const x = d[key];
      if (!x || typeof x !== 'object') return '';
      const title = x.label || x.titulo || def;
      const texto = x.texto_pieza || x.textoPieza || '';
      const copy = x.copy_redes || x.copyRedes || '';
      const visual = x.descripcion_visual;
      const visualStr = typeof visual === 'object' && visual !== null
        ? JSON.stringify(visual, null, 2)
        : (visual || '');
      const sug = x.sugerencias || '';
      const pal = Array.isArray(x.paleta_colores) ? x.paleta_colores.join(', ') : (x.paleta_colores || '');
      const promptImg = x.prompt_imagen || x.promptImagen || '';
      const imgRaw = variantImageSrc(x);

      let imgBlock = '';
      if (imgRaw) {
        const isB64 = isPubDataImageUrl(imgRaw);
        const reg = isB64 ? registerPubB64Src(imgRaw) : null;
        const src = reg ? reg.previewSrc : imgRaw;
        const refAttr = reg ? ` data-pub-ref="${esc(reg.refId)}"` : '';
        const srcAttr = !isB64 ? ` data-src="${esc(imgRaw)}"` : '';
        const b64Attr = ` data-is-b64="${isB64}"`;
        imgBlock = `
          <div class="pub-var-visual-img-wrap">
            <img src="${esc(src)}" class="pub-var-visual-img" alt="">
          </div>
          <div class="pub-img-actions">
            <button type="button" class="pub-img-btn pub-img-view-btn"${refAttr}${srcAttr}${b64Attr} title="Ampliar">🔍 Ampliar</button>
            <button type="button" class="pub-img-btn pub-img-dl-btn"${refAttr}${srcAttr}${b64Attr} data-label="${esc(key)}" title="Descargar">⬇ Descargar</button>
          </div>`;
      } else if (x.error) {
        imgBlock = `<p class="pub-var-img-err">⚠ ${esc(String(x.error))}</p>`;
      }

      const modelLine = x.model ? `<p class="pub-var-model">${esc(x.model)}</p>` : '';

      const hasTechDetails = visualStr || promptImg;
      const techBlock = hasTechDetails ? `
        <div class="pub-tech-toggle">
          <button type="button" class="pub-tech-toggle-btn" data-expanded="false">⚙ Detalles técnicos</button>
          <div class="pub-tech-content" hidden>
            ${visualStr ? `<div class="pub-var-visual"><span class="pub-result-copy-label">Descripción visual</span><pre class="pub-json-pre">${esc(visualStr)}</pre></div>` : ''}
            ${promptImg ? `<div class="pub-prompt-img"><span class="pub-result-copy-label">Prompt imagen (EN)</span><pre class="pub-json-pre">${esc(promptImg)}</pre></div>` : ''}
          </div>
        </div>` : '';

      return `
        <div class="pub-var-card">
          <div class="pub-var-card-head">${esc(title)}</div>
          ${imgBlock}
          ${modelLine}
          ${texto ? `<div class="pub-result-text">${esc(texto)}</div>` : ''}
          ${copy ? `<div class="pub-result-copy">
            <div class="pub-copy-header">
              <span class="pub-result-copy-label">Copy redes</span>
              <button type="button" class="pub-copy-btn" data-copy-text="${esc(copy)}" title="Copiar al portapapeles">📋</button>
            </div>
            <pre class="pub-result-copy-text">${esc(copy)}</pre>
          </div>` : ''}
          ${sug ? `<div class="pub-result-hints"><strong>Sugerencias:</strong> ${esc(sug)}</div>` : ''}
          ${pal ? `<div class="pub-pal">Paleta: ${esc(pal)}</div>` : ''}
          ${techBlock}
        </div>`;
    }).join('');
    return `${metaLine}<div class="pub-variants-grid">${cards}</div>`;
  }

  /* Legacy */
  return `
    ${metaLine}
    ${d.imagen_url ? `<img src="${esc(d.imagen_url)}" class="pub-result-img" alt="">` : ''}
    ${d.texto_pieza ? `<div class="pub-result-text">${esc(d.texto_pieza)}</div>` : ''}
    ${d.copy_redes ? `<div class="pub-result-copy"><div class="pub-result-copy-label">Copy para redes</div><pre class="pub-result-copy-text">${esc(d.copy_redes)}</pre></div>` : ''}
    ${d.sugerencias ? `<div class="pub-result-hints"><strong>Sugerencias:</strong> ${esc(d.sugerencias)}</div>` : ''}`;
}

export function PublicidadView() {
  const businessId = getSelectedBusiness();
  const businessName = getSelectedBusinessName();

  let selectedTipo = null;
  let phase = 'select';
  let resultData = null;
  let historial = [];
  let nss5Engine = null;

  const container = document.createElement('div');
  container.className = 'view-container';

  function render() {
    if (nss5Engine) { nss5Engine.destroy(); nss5Engine = null; }

    container.innerHTML = `
      <div class="view-header">
        ${regresoHtml()}
        <div class="view-header-top">
          <h2 class="view-title">🎨 Publicidad</h2>
          <span class="view-subtitle">${esc(businessName || businessId)}</span>
        </div>
      </div>
      ${!businessId ? '<div class="empty-state"><p>Selecciona un negocio primero.</p></div>' : `
      <div class="pub-layout">
        <section class="pub-tipos-section">
          <div class="pub-section-title">Tipo de publicidad</div>
          <div class="pub-tipos-grid">
            ${TIPOS.map(t => `
              <button type="button" class="pub-tipo-card ${selectedTipo?.id === t.id ? 'pub-tipo-active' : ''}"
                data-tipo="${t.id}" title="${esc(t.desc)}">
                <span class="pub-tipo-icon">${t.icon}</span>
                <span class="pub-tipo-label">${esc(t.label)}</span>
                <span class="pub-tipo-desc">${esc(t.desc)}</span>
              </button>
            `).join('')}
          </div>
        </section>

        ${selectedTipo && (phase === 'form' || phase === 'generating') ? `
        <section class="pub-form-section ${phase === 'generating' ? 'pub-generating-active' : ''}">
          <div class="pub-section-title">${selectedTipo.icon} ${esc(selectedTipo.label)}</div>
          ${phase === 'generating' ? `
          <div class="pub-gen-fullscreen">
            <div class="pub-gen-status">
              <div class="pub-gen-spinner"></div>
              <p class="pub-gen-msg">Generando 3 variantes con IA…</p>
              <p class="pub-gen-hint">Esto toma entre 2 y 5 minutos. ¡Aprovecha el tiempo!</p>
            </div>
            <div class="pub-gen-activity" id="nss5-activity-slot"></div>
          </div>` : ''}
          <form id="pub-form" class="pub-form" ${phase === 'generating' ? 'hidden' : ''}>
            <div class="pub-form-hint">Instrucciones para la IA (opcional)</div>
            <textarea id="pub-instrucciones" class="form-control pub-instrucciones-ta"
              rows="3" placeholder="Ej: Tono cercano, mencionar precios en pesos colombianos…"></textarea>
            <div class="pub-form-fields">
              ${selectedTipo.campos.map(c => `
                <div class="pub-field">
                  <label class="pub-field-label">${esc(c.label)}</label>
                  ${c.type === 'textarea'
                    ? `<textarea name="${c.key}" class="form-control form-control-sm" rows="3" placeholder="${esc(c.placeholder || '')}"></textarea>`
                    : `<input type="${c.type}" name="${c.key}" class="form-control form-control-sm" placeholder="${esc(c.placeholder || '')}">`
                  }
                </div>
              `).join('')}
            </div>
            <div class="pub-form-actions">
              <button type="button" class="btn btn-ghost btn-sm" id="pub-btn-back">&larr; Cambiar tipo</button>
              ${PREFILLS[selectedTipo.id] ? `<button type="button" class="btn btn-ghost btn-sm" id="pub-btn-prefill" style="border-color:var(--accent-blue);color:var(--accent-blue)">Llenar ejemplo demo</button>` : ''}
              <button type="submit" class="btn btn-primary" id="pub-btn-generate">Generar 3 variantes &rarr;</button>
            </div>
          </form>
        </section>
        ` : ''}

        ${phase === 'result' && resultData ? `
        <section class="pub-result-section">
          <div class="pub-section-title">Resultado generado</div>
          <div class="pub-result-content">${buildResultSection(resultData)}</div>
          <div class="pub-result-actions">
            <button class="btn btn-ghost btn-sm" id="pub-btn-regenerate">🔄 Otra generación</button>
            <button class="btn btn-primary" id="pub-btn-save">💾 Listo (historial actualizado)</button>
            <button class="btn btn-ghost btn-sm" id="pub-btn-nueva">+ Nueva pieza</button>
          </div>
        </section>
        ` : ''}

        ${phase === 'saved' ? `
        <div class="pub-saved-banner">✅ Generación registrada. Revisá el historial abajo.</div>
        <button class="btn btn-primary btn-sm" id="pub-btn-nueva2" style="margin-bottom:1rem">+ Nueva pieza</button>
        ` : ''}

        <section class="pub-hist-section">
          <div class="pub-section-title">Historial de piezas</div>
          ${historial.length ? `
            <div class="pub-hist-grid">
              ${historial.map((h, i) => `
                <div class="pub-hist-card pub-hist-card--clickable" data-hist-idx="${i}" title="Ver esta pieza">
                  ${h.imagen_resultado_url ? `<img src="${esc(h.imagen_resultado_url)}" class="pub-hist-thumb" alt="">` : '<div class="pub-hist-thumb-ph">🎨</div>'}
                  <div class="pub-hist-info">
                    <span class="pub-hist-tipo">${esc(h.tipo_publicidad ?? h.tipoPublicidad ?? '')}</span>
                    <span class="pub-hist-date">${formatearFecha(h.created_at ?? h.createdAt)}</span>
                  </div>
                </div>
              `).join('')}
            </div>
          ` : '<div class="pub-hist-empty">No hay piezas generadas aún.</div>'}
        </section>
      </div>`}`;

    if (phase === 'generating') {
      const slot = container.querySelector('#nss5-activity-slot');
      if (slot) {
        buildLearningEngine(slot, {
          userId: jwtUserId(),
          context: 'publicidad',
          fallbackQuestions: QUIZ_BANK.filter(q => q.cat === 'marketing')
        }).then(engine => { nss5Engine = engine; });
      }
    }

    bindEvents();
  }

  function bindEvents() {
    container.querySelectorAll('.pub-tipo-card').forEach(btn => {
      btn.addEventListener('click', () => {
        selectedTipo = TIPOS.find(t => t.id === btn.dataset.tipo);
        phase = 'form';
        render();
      });
    });

    container.querySelector('#pub-btn-back')?.addEventListener('click', () => { phase = 'select'; render(); });

    container.querySelector('#pub-btn-prefill')?.addEventListener('click', () => {
      if (!selectedTipo || !PREFILLS[selectedTipo.id]) return;
      const pf = PREFILLS[selectedTipo.id];
      const form = container.querySelector('#pub-form');
      if (!form) return;
      Object.entries(pf).forEach(([key, val]) => {
        if (key === 'instrucciones') {
          const ta = container.querySelector('#pub-instrucciones');
          if (ta) ta.value = val;
        } else {
          const inp = form.querySelector(`[name="${key}"]`);
          if (inp) inp.value = val;
        }
      });
    });

    container.querySelector('#pub-form')?.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!selectedTipo || !businessId) return;
      phase = 'generating';
      render();

      const form = container.querySelector('#pub-form');
      const instrucciones = container.querySelector('#pub-instrucciones')?.value || '';
      const variables = {};
      selectedTipo.campos.forEach(c => {
        const inp = form?.querySelector(`[name="${c.key}"]`);
        if (inp) variables[c.key] = inp.value || null;
      });

      let biz = null;
      const bizRes = await BusinessApiClient.getById(businessId);
      if (bizRes.ok) {
        biz = bizRes.data?.data ?? bizRes.data;
      }
      const ac = pickBizAccount(biz);
      const logoUrl = ac.logoUrl;
      if (!logoUrl) {
        avisar('Configurá el logo del negocio en el negocio → Apariencia (logo_url).', { tono: 'error' });
        phase = 'form';
        render();
        return;
      }

      const result = await PublicidadApiClient.generate({
        businessId,
        businessName,
        businessType: ac.businessType,
        tipoPublicidad: selectedTipo.id,
        instrucciones,
        variables,
        logoUrl,
        colorPrimario: ac.colorPrimario,
        colorSecundario: ac.colorSecundario,
        colorTexto: ac.colorTexto,
        personajeUrl: ac.personajeUrl,
        personajeCategoria: ac.personajeCategoria,
        fontName: ac.fontName,
        fontUrl: ac.fontUrl,
      });

      if (result.ok) {
        resultData = result.data;
        phase = 'result';
      } else {
        const errMsg = typeof result.error === 'string' ? result.error : (result.error?.message || JSON.stringify(result.error));
        avisar('Error generando: ' + errMsg, { tono: 'error' });
        phase = 'form';
      }
      render();
    });

    container.querySelector('#pub-btn-regenerate')?.addEventListener('click', () => { phase = 'form'; render(); });

    container.querySelector('#pub-btn-save')?.addEventListener('click', async () => {
      phase = 'saved';
      await loadHistorial();
      render();
    });

    container.querySelector('#pub-btn-nueva')?.addEventListener('click', resetFlow);
    container.querySelector('#pub-btn-nueva2')?.addEventListener('click', resetFlow);

  }

  /* ─── Delegated events (registered ONCE, outside bindEvents) ─── */

  container.addEventListener('click', async (e) => {
      // Ampliar imagen
      const viewBtn = e.target.closest('.pub-img-view-btn');
      if (viewBtn) {
        const src = resolvePubSrc(viewBtn);
        if (!src) return;
        const isB64 = viewBtn.dataset.isB64 === 'true';
        if (isB64) {
          try {
            const [header, b64data] = src.split(',');
            const mime = header.split(':')[1].split(';')[0];
            const binary = atob(b64data);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
            const blobUrl = URL.createObjectURL(new Blob([bytes], { type: mime }));
            const win = window.open(blobUrl, '_blank');
            if (win) setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
          } catch { window.open(src, '_blank'); }
        } else {
          window.open(src, '_blank');
        }
        return;
      }

      // Descargar imagen
      const dlBtn = e.target.closest('.pub-img-dl-btn');
      if (dlBtn) {
        const src = resolvePubSrc(dlBtn);
        const isB64 = dlBtn.dataset.isB64 === 'true';
        const filename = `publicidad_${dlBtn.dataset.label || 'variante'}.${isB64 ? 'jpg' : 'png'}`;
        if (!src) return;
        if (isB64) {
          const a = document.createElement('a');
          a.href = src;
          a.download = filename;
          a.click();
        } else {
          const origText = dlBtn.textContent;
          dlBtn.disabled = true;
          dlBtn.textContent = '⏳';
          try {
            const blob = await blobDeUrl(src);
            if (blob) {
              const blobUrl = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = blobUrl;
              a.download = filename;
              a.click();
              setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
            } else {
              window.open(src, '_blank');
            }
          } catch { window.open(src, '_blank'); }
          finally { dlBtn.disabled = false; dlBtn.textContent = origText; }
        }
        return;
      }

      // Copiar copy redes
      const copyBtn = e.target.closest('.pub-copy-btn');
      if (copyBtn) {
        const text = copyBtn.dataset.copyText || '';
        try {
          await navigator.clipboard.writeText(text);
          const orig = copyBtn.textContent;
          copyBtn.textContent = '✅';
          setTimeout(() => { copyBtn.textContent = orig; }, 1500);
        } catch {
          const ta = document.createElement('textarea');
          ta.value = text;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          const orig = copyBtn.textContent;
          copyBtn.textContent = '✅';
          setTimeout(() => { copyBtn.textContent = orig; }, 1500);
        }
        return;
      }

      // Toggle detalles técnicos
      const techBtn = e.target.closest('.pub-tech-toggle-btn');
      if (techBtn) {
        const wrap = techBtn.closest('.pub-tech-toggle');
        const content = wrap?.querySelector('.pub-tech-content');
        if (!content) return;
        const expanded = techBtn.dataset.expanded === 'true';
        content.hidden = expanded;
        techBtn.dataset.expanded = String(!expanded);
        techBtn.textContent = expanded ? '⚙ Detalles técnicos' : '⚙ Ocultar detalles';
        return;
      }

      // Historial clickeable
      const histCard = e.target.closest('.pub-hist-card--clickable');
      if (histCard) {
        const hIdx = parseInt(histCard.dataset.histIdx, 10);
        const item = historial[hIdx];
        if (!item) return;
        // La API devuelve raw_response_json (string) o raw_response (object)
        const rawValue = item.rawResponseJson ?? item.raw_response_json ?? item.raw_response ?? item.rawResponse;
        let parsed = null;
        if (rawValue && typeof rawValue === 'object') {
          parsed = rawValue;
        } else if (typeof rawValue === 'string' && rawValue.trim()) {
          try { parsed = JSON.parse(rawValue); } catch { /* no parseable */ }
        }
        if (parsed) {
          resultData = parsed;
          phase = 'result';
          render();
        } else {
          avisar('Esta pieza no tiene datos guardados para previsualizarla.');
        }
        return;
      }
    });

  function resolvePubSrc(btn) {
    const ref = btn.dataset.pubRef;
    if (ref) {
      const dataUrl = resolvePubDataUrl(ref);
      if (dataUrl) return dataUrl;
    }
    return btn.dataset.src || '';
  }

  function resetFlow() {
    revokePubPreviewBlobs();
    selectedTipo = null;
    resultData = null;
    phase = 'select';
    render();
  }

  async function loadHistorial() {
    if (!businessId) return;
    const res = await PublicidadApiClient.list(businessId, 1, 20);
    if (res.ok) {
      const raw = res.data?.data?.items ?? res.data?.items ?? res.data ?? [];
      historial = Array.isArray(raw) ? raw : [];
    }
  }

  // PANEL-SUBS-FEATURES-001 T3 — gate por feature_key 'publicidad'
  container.innerHTML = '<div class="empty-state"><p>Verificando acceso…</p></div>';
  useFeatureGate(businessId, 'publicidad').then(gate => {
    if (gate.blocked) {
      renderFeatureGate(container, gate, { feature: 'publicidad', viewName: 'Publicidad' });
      return;
    }
    render();
    if (businessId) loadHistorial().then(() => { render(); });
  });

  return container;
}
